import fireFocusedBasedAccessibilityAlert from "../actions/fireFocusedBasedAccessibilityAlert";
import { orchestrator } from "satcheljs";

const styles = require("../components/AccessibilityAlert.scss");

// Duration to wait before initiating rendering of the alert message
// This delay appears to help ensure that screen readers pickup the alert message.  Sometimes messages
// wouldn't always get read out when many things were happening (eg, performing an action from a dialog,
// where the focus and context change and get read out at the same time).
const ACCESSIBILITY_ALERT_INITIAL_DELAY: number = 500;
// Duration to keep focus on the announcement element (enough time for VoiceOver to read it)
const FOCUS_ANNOUNCEMENT_DURATION: number = 1000;

/**
 * Creates a focusable element and focuses it to force VoiceOver to announce the message.
 * This is necessary for iframe-hosted apps where aria-live regions may not be detected by VoiceOver on Mac.
 * VoiceOver reads the accessible name of elements when they receive focus.
 */
export function focusBasedAnnouncement(alertText: string): void {
    // Create a visually hidden but focusable element
    const announceElement = document.createElement('div');
    announceElement.setAttribute('tabindex', '-1');
    announceElement.setAttribute('role', 'status');
    announceElement.setAttribute('aria-label', alertText);
    announceElement.className = styles.accessibilityAlert;

    // Append to the accessibility portal container if it exists, otherwise to body
    const accessibilityHost = document.getElementById('accessibility-host') || document.body;
    accessibilityHost.appendChild(announceElement);

    // Cancel any pending announcements by removing existing announceElement if present
    const existingAnnounceElement = document.querySelector(`.${styles.accessibilityAlert}`);
    if (existingAnnounceElement) {
        existingAnnounceElement.parentNode?.removeChild(existingAnnounceElement);
    }

    // Use requestAnimationFrame to ensure DOM is ready before focusing
    requestAnimationFrame(() => {
        // Use preventScroll to avoid scrolling the page when focusing the hidden element
        announceElement.focus({ preventScroll: true });

        // Clean up after VoiceOver has had time to read the announcement
        setTimeout(() => {
            announceElement.blur(); // Clear focus before removing the element
            if (announceElement.parentNode) {
                announceElement.parentNode.removeChild(announceElement);
            }
        }, FOCUS_ANNOUNCEMENT_DURATION);
    });
}

/**
 * Fire an accessibility alert message using focus-based announcement for VoiceOver support in iframes.
 * This also triggers the standard aria-live region announcement for other screen readers.
 */
export default orchestrator(fireFocusedBasedAccessibilityAlert, actionMessage => {
    setTimeout(() => {
        const alertText = actionMessage.alertText;

        // Use focus-based announcement for VoiceOver support in iframes
        focusBasedAnnouncement(alertText);
    }, ACCESSIBILITY_ALERT_INITIAL_DELAY);
});
